/**
 * Where "Back" goes when leaving the transcript editor.
 *
 * The editor is reached from more than one place — the transcripts list, and an
 * incident's Transcripts card, which links a clip's transcript directly — so a
 * fixed destination is wrong for somebody either way. Back means BACK.
 *
 * Pure, and separate from the page, because the interesting part is a three-way
 * read of browser state that a component test cannot reproduce faithfully.
 */

export interface EditorHistoryState {
  /** React-router's entry index. Corroborating only — see below. */
  idx?: number;
  /** The dirty-draft guard's same-URL sentinel entry. */
  transcriptEditGuard?: boolean;
}

export type EditorExit =
  | { type: "pop"; delta: -1 | -2 }
  | { type: "replace"; to: string };

export function resolveEditorExit(args: {
  /** `useLocation().key`. Only the session's first entry keeps "default". */
  locationKey: string;
  /** `window.history.state`. */
  historyState: EditorHistoryState | null;
  /** Origin recorded by the caller that opened the editor, when there was one. */
  returnTo: string | null;
}): EditorExit {
  const { locationKey, historyState, returnTo } = args;

  // `key` decides, `idx` corroborates. Every entry the router pushes carries a
  // generated key, so anything but "default" means there IS something behind
  // us. `idx` is a plain object field anyone can overwrite — the auth-callback
  // sanitize in main.tsx replaces the whole history state, and once `idx` is
  // gone every later entry inherits the damage — so deciding on it alone sent
  // officers arriving from an incident to the transcripts LIST instead of back.
  const hasPrevious =
    locationKey !== "default" ||
    (typeof historyState?.idx === "number" && historyState.idx > 0);

  if (hasPrevious) {
    // The dirty guard parks the browser on a same-URL sentinel, so leaving from
    // there costs two pops: one to consume the sentinel, one to actually leave.
    return { type: "pop", delta: historyState?.transcriptEditGuard ? -2 : -1 };
  }

  // Nothing to pop — a deep link, or a fresh tab. Replace rather than push so
  // the entry we are standing on (the sentinel, if the draft is dirty) does not
  // linger behind the destination as a dead back-press.
  return { type: "replace", to: returnTo ?? "/transcripts" };
}
